import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as NotebookCellActions from './notebook-cell.actions';
import { AccountFacade } from '@mode/accounts/contract';
import { catchError, concatMap, EMPTY, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import { NotebookCellClientService } from '@mode/notebooks/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { NotebookStateService } from '../notebook-state.service';
import { ContentType } from '@mode/notebooks/contract';
import { CellExportActions } from '@mode/notebooks/contract';
import { Action, Store } from '@ngrx/store';

@Injectable({ providedIn: 'root' })
export class NotebookCellEffects {
  constructor(
    private actions$: Actions,
    private accountFacade: AccountFacade,
    private reportFacade: ReportFacade,
    private notebookCellClientService: NotebookCellClientService,
    private featureFlagFacade: FeatureFlagsFacade,
    private notebookStateService: NotebookStateService
  ) {}

  addCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.addCell),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([_, __, ___, migrationFlagEnable]) => migrationFlagEnable),
      concatMap(([action, organization, reportToken]) => {
        const { notebookToken, content, contentType, nextToken } = action;
        const payload = {
          content: content || '',
          contentType: contentType || ContentType.Python,
          ...(nextToken ? { nextToken } : {}),
        };
        return this.notebookCellClientService
          .addCell({
            accountToken: organization.username,
            reportToken,
            notebookToken,
            payload,
          })
          .pipe(
            map((res) =>
              NotebookCellActions.addCellSuccess({
                notebookToken: action.notebookToken,
                cellResource: res,
                ...(nextToken ? { nextToken } : {}),
              })
            ),
            catchError((error) =>
              of(NotebookCellActions.addCellFailure({ notebookToken: action.notebookToken, error }))
            )
          );
      })
    )
  );

  updateCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.updateCell),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookStateService.cellResourcesArray$,
        this.notebookStateService.cellContentsArray$,
        this.notebookStateService.cellContentTypesArray$,
        this.notebookStateService.notebookToken$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([, , , , , , , migrationFlagEnable]) => migrationFlagEnable),
      map(([action, org, reportToken, cellResources, contents, contentTypes, notebookToken]) => {
        // Handle Immutable Maps from legacy selectors
        const resource = (cellResources as any)?.get
          ? (cellResources as any).get(action.cellToken)
          : cellResources[action.cellToken];
        const content = (contents as any)?.get ? (contents as any).get(action.cellToken) : contents[action.cellToken];
        const content_type = (contentTypes as any)?.get
          ? (contentTypes as any).get(action.cellToken)
          : contentTypes[action.cellToken];

        return {
          org,
          reportToken,
          notebookToken: notebookToken,
          cellToken: action.cellToken,
          resource,
          content,
          content_type,
        };
      }),
      filter((update) => {
        if (!update.resource) return false;
        return update.resource.content !== update.content || update.resource.content_type !== update.content_type;
      }),
      switchMap((update) => {
        const body = {
          content: update.content,
          contentType: update.content_type,
          frozen_in_layout: update.resource.frozen_in_layout,
          nextToken: update.resource.next_cell_token,
        };

        return this.notebookCellClientService
          .updateCell({
            accountToken: update.org.username,
            reportToken: update.reportToken,
            notebookToken: update.notebookToken,
            cellToken: update.cellToken,
            payload: body,
          })
          .pipe(
            map((cellResource) => NotebookCellActions.updateCellSuccess({ cellResource })),
            catchError((error) =>
              of(
                NotebookCellActions.updateCellFailure({ cellToken: update.cellToken, original: update.resource, error })
              )
            )
          );
      })
    )
  );

  moveCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.moveCell),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookStateService.notebookCellTokensArray$,
        this.notebookStateService.cellResourcesArray$,
        this.notebookStateService.nextCellToken$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([, , , , , , migrationFlagEnable]) => migrationFlagEnable),
      switchMap(([action, org, reportToken, cellTokens, cellResources, originalNextToken]) => {
        const index = cellTokens.indexOf(action.cellToken);
        let resource = cellResources[action.cellToken];
        let nextToken = cellTokens[index + 1];

        if (action.down) {
          // Take the now previous cell and set its next cell token to the current cell
          resource = cellResources[cellTokens[index - 1]];
          nextToken = action.cellToken;
        }

        if (index >= 0 && nextToken && resource) {
          const body = {
            content: resource.content,
            content_type: resource.content_type,
            frozen_in_layout: resource.frozen_in_layout,
            nextToken: nextToken,
          };

          return this.notebookCellClientService
            .updateCell({
              accountToken: org.username,
              reportToken,
              notebookToken: action.notebookToken,
              cellToken: resource.token,
              payload: body,
            })
            .pipe(
              map((cellResource) => NotebookCellActions.updateCellSuccess({ cellResource })),
              catchError((error) =>
                of(
                  NotebookCellActions.moveCellFailure({
                    error,
                    cellToken: action.cellToken,
                    nextCellToken: originalNextToken,
                    notebookToken: action.notebookToken,
                  })
                )
              )
            );
        }

        return EMPTY;
      })
    )
  );

  toggleFreezeCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.toggleFreezeCell),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookStateService.cellResourcesArray$,
        this.notebookStateService.notebookToken$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([_, __, ___, ____, _____, migrationFlagEnable]) => migrationFlagEnable),
      switchMap(([action, org, reportToken, cellResources, notebookToken]) => {
        const resource = cellResources[action.cellToken];
        if (!resource) return EMPTY;

        const body = {
          content: resource.content,
          content_type: resource.content_type,
          frozen_in_layout: !resource.frozen_in_layout, // Toggle the current state
          nextToken: resource.next_cell_token,
        };

        return this.notebookCellClientService
          .updateCell({
            accountToken: org.username,
            reportToken,
            notebookToken,
            cellToken: action.cellToken,
            payload: body,
          })
          .pipe(
            map((cellResource) => NotebookCellActions.updateCellSuccess({ cellResource })),
            catchError((error) =>
              of(NotebookCellActions.toggleFreezeCellFailure({ error, cellToken: action.cellToken }))
            )
          );
      })
    )
  );

  foldCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.foldCell),
      withLatestFrom(this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)),
      filter(([_, migrationFlagEnable]) => migrationFlagEnable),
      map(([action]) => NotebookCellActions.foldCellSuccess({ cellToken: action.cellToken }))
    )
  );

  addCellToLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.addCellToLayout),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([_, __, ___, migrationFlagEnable]) => migrationFlagEnable),
      switchMap(([action, org, reportToken]) => {
        return this.notebookCellClientService.addCellToLayout(org.username, reportToken, action.cellToken).pipe(
          map((vizResource) =>
            NotebookCellActions.addCellToLayoutSuccess({
              cellToken: action.cellToken,
              vizResource,
            })
          ),
          catchError((error) => of(NotebookCellActions.addCellToLayoutFailure({ cellToken: action.cellToken, error })))
        );
      })
    )
  );

  removeCellFromLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.removeCellFromLayout),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([_, __, ___, migrationFlagEnable]) => migrationFlagEnable),
      switchMap(([action, org, reportToken]) => {
        // Skip API call if no vizToken (cell wasn't in layout)
        if (!action.vizToken) {
          return of(
            NotebookCellActions.removeCellFromLayoutSuccess({
              cellToken: action.cellToken,
              vizToken: action.vizToken,
            })
          );
        }

        return this.notebookCellClientService.removeCellFromLayout(org.username, reportToken, action.vizToken).pipe(
          map(() =>
            NotebookCellActions.removeCellFromLayoutSuccess({
              cellToken: action.cellToken,
              vizToken: action.vizToken,
            })
          ),
          catchError((error) =>
            of(
              NotebookCellActions.removeCellFromLayoutFailure({
                cellToken: action.cellToken,
                vizToken: action.vizToken,
                error,
              })
            )
          )
        );
      })
    )
  );

  destroyCell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookCellActions.destroyCell),
      withLatestFrom(
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1),
        this.notebookStateService.cellResourcesArray$,
        this.notebookStateService.notebookCellTokensArray$,
        this.notebookStateService.cellLastRunTokensArray$,
        this.notebookStateService.vizCellTokensArray$,
        this.notebookStateService.runsResultTokensArray$,
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookStateService.notebookToken$
      ),
      filter(([_, migrationFlagEnable]) => migrationFlagEnable),
      switchMap(
        ([
          action,
          migrationFlagEnable,
          cells,
          cellTokensByNotebook,
          lastRunTokens,
          vizTokens,
          resultTokensByRun,
          org,
          reportToken,
          notebookToken,
        ]) => {
          const cell = cells[action.cellToken];
          const runToken = lastRunTokens[action.cellToken];
          const vizToken = vizTokens[action.cellToken];
          const resultTokens = resultTokensByRun[runToken];

          if (!cell) return EMPTY;

          return this.notebookCellClientService.destroyCell(org.username, reportToken, notebookToken, cell.token).pipe(
            switchMap(() => {
              const actions: Action[] = [];

              actions.push(
                NotebookCellActions.removeCellFromLayout({ cellToken: action.cellToken, vizToken }),
                NotebookCellActions.destroyCellSuccess({
                  cellToken: action.cellToken,
                  notebookToken: notebookToken,
                  runToken,
                  resultTokens,
                })
              );

              const cellTokens = cellTokensByNotebook[notebookToken] || [];
              if (cellTokens.length === 0) {
                actions.push(NotebookCellActions.addCell({ notebookToken: notebookToken }));
              }

              if (cell.local_dataset_exported_at) {
                actions.push(CellExportActions.refreshDataViewAfterCellUpdate({ cellToken: action.cellToken }));
              }

              return of(...actions);
            }),
            catchError((error) =>
              of(
                NotebookCellActions.destroyCellFailure({
                  cellToken: action.cellToken,
                  notebookToken: action.notebookToken,
                  error,
                })
              )
            )
          );
        }
      )
    )
  );
}
